import React, {useEffect, useRef, useState, useCallback} from "react";
import {CSSTransition} from "react-transition-group";
import {useHudOverlay} from "widgets/hud/model/overlayStore";
import {
    AnimatedWrap,
    SearchInputSuffix,
    SearchInputWrapper,
    SearchModalContent,
    SearchModalHeader
} from "./styles";
import {useOutClick} from "app/lib/hooks/useOutClick";
import {Input} from "@atomaro/ui-kit";
import {SearchIcon} from "shared/ui/icons/searchIcon";
import {Overlay} from "shared/ui/drawer/style.ts";
import {CloseIcon} from "shared/ui/icons";
import {SearchHistory} from "widgets/searchModal/ui/SearchHistory.tsx";
import {SearchBlock} from "widgets/searchModal/ui/SearchBlock.tsx";
import {useUserStore} from "entities/user";
import {$api} from "shared/api";
import {useResize} from "../../../app/lib/hooks/useResize.ts";

const ENTRY_ID = "searchHistory";

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
    return (value: T) => {
        for (const ref of refs) {
            if (!ref) continue;
            if (typeof ref === "function") ref(value);
            else (ref as any).current = value;
        }
    };
}

export const SearchModal = () => {
    const openId = useHudOverlay((s) => s.openId);
    const close = useHudOverlay((s) => s.close);
    const getMyInfo = useUserStore((s) => s.getMyInfo);
    const userInfo = getMyInfo();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const overlayRef = useRef<HTMLDivElement | null>(null);

    const lastConfirmedQueryRef = useRef<string | null>(null);
    const resetChainRef = useRef<boolean>(false);

    useOutClick(containerRef, close);
    const isOpen = openId === "search";

    const [searchInput, setSearchInput] = useState<string>("");
    const [searchResults, setSearchResults] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showHistory, setShowHistory] = useState<boolean>(true);
    const [history, setHistory] = useState<string[]>([]);

    const fetchHistory = useCallback(async () => {
        if (!userInfo?.id) return;
        try {
            const res = await $api.get(`/stash/entry/${ENTRY_ID}`);
            if (Array.isArray(res.data?.data)) {
                setHistory(res.data.data.slice(0, 5));
            } else {
                setHistory([]);
            }
        } catch {
            setHistory([]);
        }
    }, [userInfo]);

    const saveHistory = useCallback(async (newHistory: string[]) => {
        if (!userInfo?.id) return;
        try {
            await $api.post("/stash", {
                userId: userInfo.id,
                entryId: ENTRY_ID,
                data: newHistory
            });
            setHistory(newHistory.slice(0, 5));
        } catch (e) {
            console.error("Failed to save history", e);
        }
    }, [userInfo]);

    useEffect(() => {
        if (isOpen) fetchHistory();
    }, [isOpen, fetchHistory]);

    const updateHistoryChain = useCallback(
        (query: string) => {
            if (!query.trim()) return;
            let newHistory = [...history];
            if (!resetChainRef.current && lastConfirmedQueryRef.current) {
                newHistory = newHistory.filter(
                    (q) => q !== lastConfirmedQueryRef.current
                );
            }
            newHistory = [query, ...newHistory.filter((q) => q !== query)];
            saveHistory(newHistory);
            lastConfirmedQueryRef.current = query;
            resetChainRef.current = false;
        },
        [history, saveHistory]
    );

    const eraseHistory = useCallback(() => saveHistory([]), [saveHistory]);

    const deleteHistoryItem = useCallback(
        (item: string) => {
            const newHistory = history.filter((h) => h !== item);
            saveHistory(newHistory);
        },
        [history, saveHistory]
    );

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [isOpen, close]);

    const fetchResults = useCallback(
        async (query: string, explain?: 'hives' | 'diaries' | 'events') => {
            if (!query.trim()) {
                setSearchResults(null);
                setShowHistory(true);
                resetChainRef.current = true;
                return;
            }
            if (!explain) {
                setIsLoading(true);
            }
            try {
                const params: Record<string, string> = { 
                    search: query, 
                    userId: userInfo?.id || '' 
                };
                if (explain) {
                    params.explain = explain;
                }
                const res = await $api.get("/search", { params });
                
                if (explain) {
                    // Обновляем только конкретную категорию с новыми данными
                    setSearchResults((prev: any) => {
                        if (!prev) return { [explain]: res.data[explain] || [] };
                        return {
                            ...prev,
                            [explain]: res.data[explain] || []
                        };
                    });
                } else {
                    // Обновляем все результаты
                    setSearchResults(res.data);
                    setShowHistory(false);
                    updateHistoryChain(query);
                }
            } catch (e) {
                console.error(e);
                if (!explain) {
                    setSearchResults(null);
                }
            } finally {
                if (!explain) {
                    setIsLoading(false);
                }
            }
        },
        [userInfo, updateHistoryChain]
    );

    useEffect(() => {
        const handler = setTimeout(() => {
            if (!searchInput.trim()) {
                setSearchResults(null);
                setShowHistory(true);
                resetChainRef.current = true;
                return;
            }
            fetchResults(searchInput);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchInput]);

    const handleSubmit = () => {
        if (!searchInput.trim()) {
            setSearchResults(null);
            setShowHistory(true);
            resetChainRef.current = true;
            return;
        }
        fetchResults(searchInput);
    };

    const onInput = (value: string) => {
        setSearchInput(value);
        if (!value.trim()) {
            setSearchResults(null);
            setShowHistory(true);
            resetChainRef.current = true;
        }
    };

    const {isScreenMd} = useResize();

    return (
        <>
            <CSSTransition
                in={isOpen}
                timeout={200}
                unmountOnExit
                classNames="drawer-fade"
                nodeRef={overlayRef}
            >
                <Overlay onClick={close} ref={overlayRef}/>
            </CSSTransition>

            <CSSTransition
                in={isOpen}
                timeout={{enter: 200, exit: 150}}
                classNames="hud-slide"
                unmountOnExit
                nodeRef={nodeRef}
            >
                <AnimatedWrap
                    ref={mergeRefs(containerRef, nodeRef)}
                    role="dialog"
                    aria-modal="true"
                >
                    {
                        isScreenMd && (
                            <SearchModalHeader>
                                <h3>Поиск</h3>
                                <span onClick={close}>
                                    <CloseIcon/>
                                </span>
                            </SearchModalHeader>
                        )
                    }
                    <SearchModalContent>
                        <SearchInputWrapper>
                            <Input
                                size="m"
                                placeholder="Ульи, записи, события"
                                iconPrefix={<SearchIcon/>}
                                onClickIconSuffix={() => onInput("")}
                                onClickIconPrefix={handleSubmit}
                                iconSuffix={searchInput && isScreenMd && <SearchInputSuffix/>}
                                autoFocus
                                value={searchInput}
                                onChange={(e) => onInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                            />

                            {!isScreenMd && (
                                <span onClick={close}>
                                    <CloseIcon/>
                                </span>
                            )}
                        </SearchInputWrapper>

                        {showHistory && history.length > 0 && (
                            <SearchHistory
                                history={history.slice(0, 5)}
                                onHistoryClick={(v) => {
                                    onInput(v);
                                    fetchResults(v);
                                }}
                                onHistoryDelete={deleteHistoryItem}
                                onHistoryErase={eraseHistory}
                            />
                        )}

                        {!showHistory && !isLoading && searchResults && (
                            <>
                                {(!searchResults.hives?.length &&
                                    !searchResults.diaries?.length &&
                                    !searchResults.events?.length) && (
                                    <p>Результаты не найдены</p>
                                )}
                                {searchResults.hives?.length > 0 && (
                                    <SearchBlock 
                                        type="hive" 
                                        title="Ульи" 
                                        items={searchResults.hives} 
                                        onItemClick={close}
                                        onLoadMore={() => fetchResults(searchInput, 'hives')}
                                    />
                                )}
                                {searchResults.diaries?.length > 0 && (
                                    <SearchBlock 
                                        type="diary" 
                                        title="Записи" 
                                        items={searchResults.diaries} 
                                        onItemClick={close}
                                        onLoadMore={() => fetchResults(searchInput, 'diaries')}
                                    />
                                )}
                                {searchResults.events?.length > 0 && (
                                    <SearchBlock 
                                        type="events" 
                                        title="События" 
                                        items={searchResults.events} 
                                        onItemClick={close}
                                        onLoadMore={() => fetchResults(searchInput, 'events')}
                                    />
                                )}
                            </>
                        )}

                        {isLoading && <p style={{marginTop: 12}}>Загрузка...</p>}
                    </SearchModalContent>
                </AnimatedWrap>
            </CSSTransition>
        </>
    );
};
